import { List, OrderedMap, OrderedSet, Set } from "immutable";

import type { Action, MutationAction } from "./Action";
import type { ActionIntent, MutationActionIntent } from "./ActionIntent";
import type { TableMutation } from "./TableMutation";

// actions to get you to match a source of truth
export type ReconciliationApplyAction = { type: "apply"; action: Action };
// actions that you and the source of truth agree on
export type ReconciliationKnownAction = { type: "known"; intent: ActionIntent };
// generated rollback actions to remove changes you already made...
export type ReconciliationRollbackAction = { type: "rollback"; intent: ActionIntent };
// ...and the actions to potentially reapply them
export type ReconciliationRetryAction = { type: "retry"; intent: ActionIntent };
// other pending actions to try to put on top of the source of truth
export type ReconciliationPendingAction = { type: "pending"; intent: ActionIntent };

export type ReconciliationAction =
	| ReconciliationApplyAction
	| ReconciliationKnownAction
	| ReconciliationRollbackAction
	| ReconciliationRetryAction
	| ReconciliationPendingAction;

/**
 * Represents a set of reconciliation actions to converge 2 different States.
 *
 * If consuming the results as incremental changes sets, the expected order
 * to execute the actions is (by attribute name):
 *   1. rollbackActions
 *   2. applyActions
 *   3. pendingActions
 *   4. retryActions
 * If executed in this order, the result should be a correct ordering of all
 * changes necessary, including ordering dependent changes with each other.
 * There may be other correct orderings.
 */
export type ReconciliationPlan = {
	/**
	 * Get an ordered list of actions that will undo previously applied actions
	 * that have not yet be confirmed by the source of truth.
	 *
	 * If processing incrementally, this should be the 1st
	 * {ReconciliationAction} list consumed.
	 */
	rollbackActions(): List<ReconciliationRollbackAction>;
	/**
	 * Get an ordered list of actions both you and the source of truth
	 * know about already. The use of this list and the rollbackActions
	 * list are likely an either or.
	 *
	 * If processing incrementally, this should be the 1th
	 * {ReconciliatonAction} list consumed.
	 */
	knownActions(): List<ReconciliationKnownAction>;
	/** Get an ordered list of actions that must be applied according to the
	 * source of truth.
	 *
	 * If processing incrementally, this should be the 2nd
	 * {ReconciliationAction} list consumed.
	 */
	committedActions(): List<ReconciliationApplyAction>;
	/**
	 * Get an ordered list of uncontroversial actions that should be applied
	 * after catching up to the source of truth.
	 *
	 * If processing incrementally, this should be the 3rd
	 * {ReconciliationAction} list consumed.
	 */
	pendingActions(): List<ReconciliationPendingAction>;
	/**
	 * Get an ordered list of previously rolled back actions that should be applied
	 * after catching up to the source of truth.
	 *
	 * If processing incrementally, this should be the 4th
	 * {ReconciliationAction} list consumed.
	 */
	retryActions(): List<ReconciliationRetryAction>;
	/**
	 * Get an ordered list of all pending actions, both uncontroversial, and retries,
	 * that should be applied after catching up to the source of truth.
	 *
	 * If processing incrementally, this should be the 3rd
	 * {ReconciliationAction} list consumed, and neither pending nor retry
	 * actions should be consumed.
	 */
	allPendingActions(): List<ReconciliationPendingAction | ReconciliationRetryAction>;

	/** Get an ordered {ReconciliationAction} of all actions to be applied by the plan. */
	all(): List<ReconciliationAction>;
};

class ConflictIndex {
	private committedIds = Set<string>();
	private known = OrderedMap<string, ActionIntent>();
	private rollbacks = OrderedSet<ActionIntent>();
	private pending = List<ActionIntent>();

	constructor(private readonly committedActions: List<Action>) {
		this.committedIds = committedActions.map((action) => action.intentId).toSet();
	}

	isCommitted(intentId: string): boolean {
		return this.committedIds.has(intentId);
	}

	markSeen(intent: ActionIntent) {
		this.known = this.known.set(intent.action.intentId, intent);
	}

	markRolledBack(intent: ActionIntent) {
		this.rollbacks = this.rollbacks.add(intent);
	}

	markPending(intent: ActionIntent) {
		this.pending = this.pending.push(intent);
	}

	private possibleConflicts(pendingAction: Action): List<Action> {
		return this.committedActions
			.concat(this.rollbacks.map((intent) => intent.action))
			.filter(
				(action) =>
					pendingAction.intentId !== action.intentId && !this.known.has(action.intentId)
			);
	}

	any({ action: pendingAction }: ActionIntent): boolean {
		if (pendingAction.type !== "mutation") {
			return false;
		}

		const { mutation } = pendingAction;

		if (mutation.type === "DELETE" || mutation.type === "INSERT") {
			return this.possibleConflicts(pendingAction).some(
				isMutationForSameRecordAs(pendingAction)
			);
		} else if (mutation.type === "UPDATE") {
			return this.possibleConflicts(pendingAction)
				.filter(isMutationForSameRecordAs(pendingAction))
				.some(isMutationOverlapping(mutation));
		}

		return false;
	}

	getReconciliationActions(reqPendingActions: List<ActionIntent>): ReconciliationPlan {
		const pendingActionIds = reqPendingActions.map(({ action }) => action.intentId).toSet();

		const committedActions = () =>
			this.committedActions
				.filter((action: Action) => !pendingActionIds.has(action.intentId))
				.map((action) => ({ type: "apply", action }) as const);

		const rollbackActions = () =>
			this.rollbacks
				.toList()
				.map((intent) => ({ type: "rollback", intent }) as const)
				.reverse();
		const retryActions = () =>
			this.rollbacks.toList().map((intent) => ({ type: "retry", intent }) as const);

		const knownActions = () =>
			this.known
				.valueSeq()
				.toList()
				.map((intent) => ({ type: "known", intent }) as const);

		const pendingActions = () =>
			this.pending.map((intent) => ({ type: "pending", intent }) as const);

		let allPendingActions = () => {
			const pas = List<ReconciliationPendingAction | ReconciliationRetryAction>()
				.merge(
					pendingActions() as List<
						ReconciliationPendingAction | ReconciliationRetryAction
					>,
					retryActions() as List<ReconciliationPendingAction | ReconciliationRetryAction>
				)
				// TODO: do we actually need to resort the actions on their way out?
				// In order to ensure the "desiredState" of the "last" item is complete?
				.sortBy((a) =>
					a.intent.action.type === "queryRegistration"
						? a.intent.action.desiredStartEpoch
						: a.intent.action.desiredApplyEpoch
				);
			return pas;
		};

		return {
			rollbackActions,
			committedActions,
			knownActions,
			pendingActions,
			retryActions,
			allPendingActions,
			all: () =>
				List<ReconciliationAction>().merge(
					rollbackActions() as List<ReconciliationAction>,
					committedActions() as List<ReconciliationAction>,
					allPendingActions() as List<ReconciliationAction>
				),
		};
	}
}

const isMutationForSameRecordAs =
	(pendingAction: MutationAction) =>
	(otherAction: Action): otherAction is MutationAction => {
		return (
			"mutation" === otherAction.type &&
			pendingAction.table === otherAction.table &&
			pendingAction.mutation.recordId === otherAction.mutation.recordId
		);
	};

const isMutationOverlapping =
	(pendingMutation: Extract<TableMutation<unknown>, { type: "UPDATE" }>) =>
	(action: MutationAction): boolean => {
		return (
			action.mutation.type === "DELETE" ||
			action.mutation.type === "INSERT" ||
			(action.mutation.type === "UPDATE" &&
				pendingMutation.change.path === action.mutation.change.path)
		);
	};

export const reconciliationPlan = (
	sourceOfTruthActions: List<Action>,
	pendingActions: List<ActionIntent>,
	desiredHighWaterMark: ActionIntent | undefined
): ReconciliationPlan => {
	const highWaterMarkId = desiredHighWaterMark?.action.intentId;
	const conflicts = new ConflictIndex(sourceOfTruthActions);

	for (const pendingIntent of pendingActions) {
		const { action: pendingAction } = pendingIntent;

		if (conflicts.isCommitted(pendingAction.intentId)) {
			conflicts.markSeen(pendingIntent);
			continue;
		}

		if (conflicts.any(pendingIntent)) {
			conflicts.markRolledBack(pendingIntent);
			continue;
		}

		if (pendingAction.intentId === highWaterMarkId) {
			conflicts.markRolledBack(pendingIntent);
			continue;
		}

		conflicts.markPending(pendingIntent);
	}

	return conflicts.getReconciliationActions(pendingActions);
};
